import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../auth/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

/**
 * Mandatory sign-up legal-acceptance screen — Terms & Conditions, Privacy
 * Policy, Risk Disclosure, Algo-Trading Disclosure (2026-09-12).
 *
 * Sits in the same onboarding-gate sequence as ProfileSetup.jsx (see
 * ProtectedRoute.jsx: profile_complete -> legal_accepted -> active/pending).
 * Reuses the existing shadcn Dialog component for the "View full document"
 * modal rather than adding a markdown/modal dependency — full text is
 * rendered as plain pre-wrapped text.
 *
 * Only re-shows checkboxes for documents the user hasn't accepted at the
 * CURRENT version (see /api/legal/status) — so a version bump only makes a
 * returning user re-check the document(s) that actually changed.
 */
export const LegalAcceptance = () => {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();

  const [documents, setDocuments] = useState([]);
  const [status, setStatus] = useState({});
  const [checked, setChecked] = useState({});
  const [viewDoc, setViewDoc] = useState(null); // full document object being viewed in the modal
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [docsRes, statusRes] = await Promise.all([
          api.get("/legal/documents"),
          api.get("/legal/status"),
        ]);
        if (cancelled) return;
        const docs = docsRes.data?.documents || [];
        const st = statusRes.data?.status || {};
        setDocuments(docs);
        setStatus(st);
        // Pre-check anything already accepted at the current version, so a
        // returning user re-accepting after a version bump only has to
        // check the document(s) that actually changed.
        const initialChecked = {};
        docs.forEach((d) => {
          initialChecked[d.doc_type] = !!st[d.doc_type]?.accepted;
        });
        setChecked(initialChecked);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load legal documents.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (docType) => setChecked((c) => ({ ...c, [docType]: !c[docType] }));

  const viewFull = async (docType) => {
    try {
      const res = await api.get(`/legal/documents/${docType}`);
      setViewDoc(res.data);
    } catch (err) {
      toast.error("Failed to load document text.");
    }
  };

  const allChecked = documents.length > 0 && documents.every((d) => checked[d.doc_type]);

  const submit = async () => {
    if (!allChecked) {
      toast.error("Please review and accept all four documents to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/legal/accept", {
        doc_types: documents.map((d) => d.doc_type),
      });
      const u = await refresh();
      toast.success("Thanks — you're all set.");
      navigate(u?.status === "ACTIVE" ? "/dashboard" : "/pending", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record acceptance.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data">
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10" style={{ background: "var(--bg-primary)" }}>
      <div className="w-full max-w-2xl space-y-6 border border-grid p-8" data-testid="legal-acceptance-page">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
            One last step
          </div>
          <h1 className="font-heading text-2xl">Review &amp; accept the legal terms</h1>
          <p className="text-xs text-muted-2 mt-2">
            Please review and accept all four documents below to continue. These
            documents are drafts pending review by a qualified securities lawyer /
            compliance professional and are provided for transparency about how
            {" "}StoxTrade operates — not as a substitute for your own independent
            judgement about trading risk.
          </p>
        </div>

        <div className="space-y-4">
          {documents.map((doc) => (
            <label
              key={doc.doc_type}
              className="flex items-start gap-3 border border-grid p-4 cursor-pointer"
              data-testid={`legal-checkbox-${doc.doc_type}`}
            >
              <input
                type="checkbox"
                checked={!!checked[doc.doc_type]}
                onChange={() => toggle(doc.doc_type)}
                className="mt-1 h-4 w-4 shrink-0"
              />
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{doc.title}</span>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-muted-3 font-mono-data">
                    v{doc.version}
                  </span>
                </div>
                <p className="text-xs text-muted-2 whitespace-pre-line">{doc.short_text}</p>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    viewFull(doc.doc_type);
                  }}
                  className="text-[11px] uppercase tracking-[0.15em] font-mono-data underline underline-offset-2"
                  style={{ color: "var(--bull)" }}
                  data-testid={`legal-view-${doc.doc_type}`}
                >
                  View full document →
                </button>
              </div>
            </label>
          ))}
        </div>

        <button
          type="button"
          onClick={submit}
          disabled={!allChecked || submitting}
          data-testid="legal-accept-submit"
          className="w-full px-4 py-3 border text-[11px] uppercase tracking-[0.2em] font-mono-data disabled:opacity-40"
          style={{ color: "var(--bull)", borderColor: "var(--bull)" }}
        >
          {submitting ? "Saving..." : "Accept & Continue →"}
        </button>
      </div>

      <Dialog open={!!viewDoc} onOpenChange={(open) => !open && setViewDoc(null)}>
        {/* 2026-09-13 — chat: "close button should be moved outside scroll.
            If users scrolls down, he don't have to scroll up to close
            window." The X close button dialog.jsx renders is `absolute`
            relative to DialogContent itself — putting `overflow-y-auto`
            on DialogContent (as this used to) makes the button scroll
            away with the text. Fix: DialogContent no longer scrolls itself
            (flex column, fixed max-height); only the inner text wrapper
            below scrolls, so the button — a sibling of that wrapper, not
            a descendant of it — stays pinned in the corner regardless of
            scroll position. */}
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4 pr-10 shrink-0">
            <DialogTitle>
              {viewDoc?.title} {viewDoc?.version ? `(v${viewDoc.version})` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto px-6 pb-6 min-h-0">
            <pre className="whitespace-pre-wrap text-xs leading-relaxed font-sans">
              {viewDoc?.full_text}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
